import { useEffect } from "react";
import i18n from "../i18n";

const BASE_URL = "https://aypinotomat.com";

const DEFAULT_OG_IMAGE =
  `${BASE_URL}/assets/img/Aypinautomat/sliderorj.jpg`;

/* =========================================================
   META NAME
========================================================= */

function upsertMetaByName(name, content) {
  if (!content) return;

  let element = document.querySelector(
    `meta[name="${name}"]`
  );

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("name", name);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
}

/* =========================================================
   META PROPERTY
========================================================= */

function upsertMetaByProperty(property, content) {
  if (!content) return;

  let element = document.querySelector(
    `meta[property="${property}"]`
  );

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("property", property);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
}

/* =========================================================
   LINK
========================================================= */

function upsertLink(rel, href) {
  if (!href) return;

  let element = document.querySelector(
    `link[rel="${rel}"]`
  );

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
}

/* =========================================================
   JSON-LD
========================================================= */

function setJsonLd(id, data) {
  const oldScript = document.getElementById(id);

  if (oldScript) {
    oldScript.remove();
  }

  if (!data) return;

  const script = document.createElement("script");

  script.type = "application/ld+json";
  script.id = id;
  script.textContent = JSON.stringify(data);

  document.head.appendChild(script);
}

/* =========================================================
   REMOVE JSON-LD
========================================================= */

function removeJsonLd(id) {
  const element = document.getElementById(id);

  if (element) {
    element.remove();
  }
}

/* =========================================================
   CLEAN PATH
========================================================= */

function normalizePath(path) {
  if (!path || path === "/") {
    return "/";
  }

  return `/${path.replace(/^\/+|\/+$/g, "")}`;
}

/* =========================================================
   SEO HOOK
========================================================= */

export default function useSEO({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  keywords,
  type = "website",
  breadcrumbs = [],
  robots = "index, follow",
}) {

  const language =
    (i18n.resolvedLanguage || i18n.language || "tr")
      .toLowerCase()
      .split("-")[0];

  useEffect(() => {

    /* =====================================================
       URL
    ===================================================== */

    const cleanPath = normalizePath(path);

    const url =
      cleanPath === "/"
        ? `${BASE_URL}/`
        : `${BASE_URL}${cleanPath}`;

    /* =====================================================
       HTML LANGUAGE
    ===================================================== */

    document.documentElement.lang =
      language === "en" ? "en" : "tr";

    /* =====================================================
       TITLE
    ===================================================== */

    if (title) {
      document.title = title;
    }

    /* =====================================================
       BASIC META
    ===================================================== */

    upsertMetaByName(
      "description",
      description
    );

    if (keywords) {
      upsertMetaByName(
        "keywords",
        keywords
      );
    }

    upsertMetaByName(
      "robots",
      robots
    );

    /* =====================================================
       CANONICAL
    ===================================================== */

    upsertLink(
      "canonical",
      url
    );

    /* =====================================================
       OPEN GRAPH
    ===================================================== */

    upsertMetaByProperty(
      "og:site_name",
      "Aypin Otomat"
    );

    upsertMetaByProperty(
      "og:type",
      type
    );

    upsertMetaByProperty(
      "og:title",
      title
    );

    upsertMetaByProperty(
      "og:description",
      description
    );

    upsertMetaByProperty(
      "og:url",
      url
    );

    upsertMetaByProperty(
      "og:locale",
      language === "en"
        ? "en_US"
        : "tr_TR"
    );

    /* =====================================================
       OPEN GRAPH IMAGE
    ===================================================== */

    if (image) {

      upsertMetaByProperty(
        "og:image",
        image
      );

      upsertMetaByProperty(
        "og:image:secure_url",
        image
      );

      upsertMetaByProperty(
        "og:image:alt",
        title || "Aypin Otomat"
      );

      upsertMetaByProperty(
        "og:image:width",
        "1200"
      );

      upsertMetaByProperty(
        "og:image:height",
        "630"
      );

      upsertMetaByProperty(
        "og:image:type",
        "image/jpeg"
      );
    }

    /* =====================================================
       TWITTER / X
    ===================================================== */

    upsertMetaByName(
      "twitter:card",
      "summary_large_image"
    );

    upsertMetaByName(
      "twitter:title",
      title
    );

    upsertMetaByName(
      "twitter:description",
      description
    );

    if (image) {

      upsertMetaByName(
        "twitter:image",
        image
      );

      upsertMetaByName(
        "twitter:image:alt",
        title || "Aypin Otomat"
      );
    }

    /* =====================================================
       BREADCRUMB SCHEMA
    ===================================================== */

    if (
      Array.isArray(breadcrumbs) &&
      breadcrumbs.length > 0
    ) {

      const breadcrumbSchema = {

        "@context": "https://schema.org",

        "@type": "BreadcrumbList",

        itemListElement:
          breadcrumbs.map(
            (breadcrumb, index) => {

              const breadcrumbPath =
                normalizePath(
                  breadcrumb.path || "/"
                );

              const breadcrumbUrl =
                breadcrumbPath === "/"
                  ? `${BASE_URL}/`
                  : `${BASE_URL}${breadcrumbPath}`;

              return {
                "@type": "ListItem",
                position: index + 1,
                name: breadcrumb.name,
                item: breadcrumbUrl,
              };
            }
          ),
      };

      setJsonLd(
        "jsonld-breadcrumb",
        breadcrumbSchema
      );

    } else {

      removeJsonLd(
        "jsonld-breadcrumb"
      );
    }

    /* =====================================================
       CLEANUP
    ===================================================== */

    return () => {

      removeJsonLd(
        "jsonld-breadcrumb"
      );

    };

  }, [
    title,
    description,
    path,
    image,
    keywords,
    type,
    robots,
    breadcrumbs,
    language,
  ]);
}